import React, { useState, useEffect } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import {
  Box, Typography, Card, CardContent, Stack, Chip, TextField, MenuItem, Grid, Alert,
  LinearProgress, Divider, Button, FormControlLabel, Switch, Dialog, DialogTitle,
  DialogContent, DialogActions, Link,
} from '@mui/material';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import PlaceIcon from '@mui/icons-material/Place';
import StarIcon from '@mui/icons-material/Star';
import {
  VENDOR_OFFERING_FIELD_DEFS_QUERY, FAMILY_VENDOR_OFFERINGS_QUERY, ALL_SERVICE_CATEGORIES_QUERY,
  SET_VENDOR_OFFERING_ACTIVE_MUTATION, DELETE_VENDOR_OFFERING_MUTATION, MY_VENDOR_SELECTIONS_QUERY,
  MY_BOOKABLE_SERVICES_QUERY, FAMILY_BUDGET_PLAN_QUERY, MY_PREFERRED_VENDORS_QUERY,
} from '../graphql/queries';
import { EventCityField, RadiusChips, DEFAULT_RADIUS, distanceLabel } from '../components/LocationFilter';
import { useAuth } from '../context/AuthContext';
import { fieldsForCategory, OfferingDetailList, formatPriceRange } from '../components/OfferingDetails';
import { packageLabelsFor, OfferingPackagesView } from '../components/OfferingPackages';
import { BookVendorDialog, BookingStatusChip, fmtBookingDate } from '../components/VendorBooking';
import { useFamily } from '../context/FamilyContext';
import { useNavigate } from 'react-router-dom';

import Paged from '../components/Paged'; // lists beyond 10 are paged
// Families see and book vendors only for the services on a contract they have signed; admins
// and matchmakers managing a family see that family's view and book for it. Without a family
// selected, admins and matchmakers browse every live vendor. Admins additionally see
// unpublished offerings and can deactivate or delete them.
export default function ServiceProvidersPage() {
  const { user } = useAuth();
  const isAdmin = user?.role?.name === 'system_admin';
  const isFamilyUser = ['bride_family', 'groom_family'].includes(user?.role?.name);
  const { family, setFamily } = useFamily();
  const navigate = useNavigate();
  // Families book for themselves; admins/matchmakers book for the family they're managing
  const bookingFor = isFamilyUser ? null : family;
  const canBook = isFamilyUser || Boolean(family);
  const [booking, setBooking] = useState(null); // { offering, packageId }
  const [booked, setBooked] = useState(null);
  const { data: selData, refetch: refetchSelections } = useQuery(MY_VENDOR_SELECTIONS_QUERY, {
    variables: { includeClosed: false }, skip: !canBook, fetchPolicy: 'cache-and-network',
  });
  const openBookings = (selData?.myVendorSelections || []).filter(b => ['requested', 'confirmed'].includes(b.status));
  const [category, setCategory] = useState('');
  const [city, setCity] = useState('');
  const [search, setSearch] = useState('');
  const [showUnpublished, setShowUnpublished] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  // The function place (defaults to the family's, from its Service Wizard wishlist) and the
  // radius vendors are shown within; null radius = any distance, nearest first
  const [near, setNear] = useState('');
  const [radiusKm, setRadiusKm] = useState(DEFAULT_RADIUS);
  const { data: planData } = useQuery(FAMILY_BUDGET_PLAN_QUERY, { skip: !canBook, fetchPolicy: 'cache-and-network' });
  const familyPlace = planData?.familyBudgetPlan?.eventCity || '';
  useEffect(() => { setNear(familyPlace); }, [familyPlace]);

  const { data: bookableData } = useQuery(MY_BOOKABLE_SERVICES_QUERY, { skip: !canBook, fetchPolicy: 'cache-and-network' });
  const bookable = canBook ? bookableData?.myBookableServices : null;
  const contracted = bookable?.categories || [];
  const whose = bookingFor ? `${bookingFor.displayName}'s` : 'your';

  const { data: defsData } = useQuery(VENDOR_OFFERING_FIELD_DEFS_QUERY);
  const { data: catData } = useQuery(ALL_SERVICE_CATEGORIES_QUERY, { variables: { activeOnly: true } });
  const { data, loading, error, refetch } = useQuery(FAMILY_VENDOR_OFFERINGS_QUERY, {
    variables: {
      category: category || null, city: city.trim() || null, search: search.trim() || null,
      near: near.trim() || null, radiusKm: near.trim() ? radiusKm : null,
      includeUnpublished: isAdmin && showUnpublished,
    },
    fetchPolicy: 'cache-and-network',
  });
  const [setActive] = useMutation(SET_VENDOR_OFFERING_ACTIVE_MUTATION, { onCompleted: () => refetch() });
  const [deleteOffering] = useMutation(DELETE_VENDOR_OFFERING_MUTATION, { onCompleted: () => { setConfirmDelete(null); refetch(); } });

  const fieldDefs = defsData?.vendorOfferingFields;
  const allCategories = catData?.allServiceCategories || [];
  // A family chooses only among its contracted services
  const categories = canBook ? allCategories.filter(c => contracted.includes(c.name)) : allCategories;
  const categoryLabel = (name) => allCategories.find(c => c.name === name)?.displayName || name;
  // The family's preferred vendors from the Service Wizard come first, ready to book
  const { data: prefData } = useQuery(MY_PREFERRED_VENDORS_QUERY, { skip: !canBook, fetchPolicy: 'cache-and-network' });
  const preferredByOffering = new Map((prefData?.myPreferredVendors || []).map(p => [p.offeringId, p]));
  const offerings = [...(data?.vendorOfferings || [])]
    .sort((a, b) => Number(preferredByOffering.has(b.id)) - Number(preferredByOffering.has(a.id)));
  const nothingContracted = Boolean(bookable) && contracted.length === 0;

  return (
    <Box>
      <Box mb={3}>
        <Typography variant="h4" fontWeight={700}>Service Providers</Typography>
        <Typography variant="body2" color="text.secondary">
          {canBook
            ? `Vendors for the services on ${whose} signed contract. Pick one and book it for your date.`
            : 'Vendors under a signed contract and the services they offer.'}
        </Typography>
      </Box>

      {bookable && contracted.length > 0 && (
        <Alert severity="info" sx={{ mb: 2 }}
          action={bookingFor && (
            <Button color="inherit" size="small" onClick={() => setFamily(null)}>Show all vendors</Button>
          )}>
          <Typography variant="body2" mb={0.5}>Showing vendors for {whose} signed services:</Typography>
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
            {contracted.map(c => <Chip key={c} size="small" label={categoryLabel(c)} />)}
          </Stack>
        </Alert>
      )}
      {nothingContracted && (
        <Alert severity="warning" sx={{ mb: 2 }}
          action={isFamilyUser
            ? <Button color="inherit" size="small" onClick={() => navigate('/dashboard/contracts')}>Contracts</Button>
            : <Button color="inherit" size="small" onClick={() => setFamily(null)}>Show all vendors</Button>}>
          {/* Vendors open after: family signs → admin approves (countersigns) → family accepts */}
          {bookable.awaitingAcceptance
            ? `The contract is signed and approved. Vendors open once ${isFamilyUser ? 'you accept it' : `${bookingFor.displayName} accepts it`} in Contracts.`
            : bookable.awaitingApproval
              ? `${isFamilyUser ? 'You have' : `${bookingFor.displayName} has`} signed the contract. It is waiting for the admin's approval; then ${isFamilyUser ? 'you accept it' : 'the family accepts it'} to open the vendors.`
              : bookable.awaitingSignature
                ? `Vendors open up once ${isFamilyUser ? 'you sign your' : `${bookingFor.displayName} signs their`} contract, the admin approves it and ${isFamilyUser ? 'you accept' : 'they accept'} it. It is waiting in Contracts.`
                : `Vendors open up once a contract for ${whose} selected services is signed, approved by the admin and accepted. Start with the Service Wizard.`}
        </Alert>
      )}

      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField select size="small" label="Service" value={category} onChange={e => setCategory(e.target.value)} fullWidth>
                <MenuItem value="">{canBook ? 'All contracted services' : 'All services'}</MenuItem>
                {categories.map(c => <MenuItem key={c.name} value={c.name}>{c.displayName}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField size="small" label="City" value={city} onChange={e => setCity(e.target.value)} fullWidth />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField size="small" label="Search" value={search} onChange={e => setSearch(e.target.value)} fullWidth placeholder="Name, menu or dish" />
            </Grid>
            <Grid item xs={12} md={5}>
              <EventCityField size="small" label="Function place" value={near} onChange={setNear} />
            </Grid>
            <Grid item xs={12} md={7}>
              {near.trim() ? (
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                  <Typography variant="body2" color="text.secondary">Within</Typography>
                  <RadiusChips value={radiusKm} onChange={setRadiusKm} allowAny />
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary">Enter the function place to see the nearest vendors first.</Typography>
              )}
            </Grid>
            {isAdmin && (
              <Grid item xs={12} md={2}>
                <FormControlLabel
                  control={<Switch checked={showUnpublished} onChange={e => setShowUnpublished(e.target.checked)} />}
                  label="Show unpublished"
                />
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>

      {!canBook && !isAdmin && user?.role?.name === 'matchmaker' && (
        <Alert severity="info" sx={{ mb: 2 }}>Select a client in "Managing client" to book vendors for them.</Alert>
      )}
      {booked && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setBooked(null)}
          action={<Button color="inherit" size="small" onClick={() => navigate('/dashboard/my-vendors')}>My Vendors</Button>}>
          {booked.count > 1 ? `${booked.count} booking requests` : 'Booking request'} sent to {booked.businessName} for {fmtBookingDate(booked.requiredDate)}. They'll be notified and confirm it.
        </Alert>
      )}
      {loading && <LinearProgress sx={{ mb: 2 }} />}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error.message}</Alert>}
      {!loading && !error && !offerings.length && !nothingContracted && (
        <Alert severity="info"
          action={near.trim() && radiusKm != null && (
            <Button color="inherit" size="small" onClick={() => setRadiusKm(null)}>Any distance</Button>
          )}>
          {near.trim() && radiusKm != null
            ? `No service providers within ${radiusKm} km of ${near.trim()} match these filters. Widen the distance to see vendors further away.`
            : 'No service providers match these filters yet.'}
        </Alert>
      )}

      <Grid container spacing={2}>
        <Paged items={offerings}>{rows => rows.map(o => (
          <Grid item xs={12} lg={6} key={o.id}>
            <Card variant="outlined" sx={{ height: '100%', opacity: o.isPublished ? 1 : 0.8, ...(preferredByOffering.has(o.id) ? { borderColor: 'primary.main', borderWidth: 2 } : {}) }}>
              <CardContent>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap mb={0.5}>
                  <Chip size="small" color="primary" label={o.categoryName} />
                  {preferredByOffering.has(o.id) && (
                    <Chip size="small" color="secondary" icon={<StarIcon />}
                      label={`Your preferred vendor${preferredByOffering.get(o.id).packageName ? ` · ${preferredByOffering.get(o.id).packageName}` : ''}`} />
                  )}
                  {isAdmin && !o.isPublished && <Chip size="small" variant="outlined" label={o.visibilityNote} />}
                </Stack>
                <Typography variant="h6" fontWeight={700}>{o.title}</Typography>
                <Typography variant="subtitle2" color="text.secondary">{o.businessName}</Typography>
                <Typography variant="body1" fontWeight={600} color="primary.main" mt={0.5}>{formatPriceRange(o)}</Typography>
                <Typography variant="body2" sx={{ my: 1, whiteSpace: 'pre-line' }}>{o.description}</Typography>
                {o.cancellationPolicy && (
                  <Alert severity="info" icon={false} sx={{ mb: 1, py: 0 }}>
                    <Typography variant="caption" fontWeight={700} display="block">Cancellation & refund terms</Typography>
                    <Typography variant="caption" sx={{ whiteSpace: 'pre-line' }}>{o.cancellationPolicy}</Typography>
                  </Alert>
                )}
                <OfferingDetailList fields={fieldsForCategory(fieldDefs, o.category)} details={o.details} />
                <OfferingPackagesView
                  labels={packageLabelsFor(fieldDefs, o.category)} packages={o.packages}
                  onBook={canBook && o.isPublished ? (p) => setBooking({ offering: o, packageId: p.id }) : undefined}
                />
                {openBookings.filter(b => b.offeringId === o.id).map(b => (
                  <Stack key={b.id} direction="row" spacing={1} alignItems="center" mt={1.5} flexWrap="wrap" useFlexGap>
                    <Typography variant="body2" fontWeight={600}>Booked for {fmtBookingDate(b.requiredDate)}{b.packageName ? ` · ${b.packageName}` : ''}</Typography>
                    <BookingStatusChip status={b.status} />
                  </Stack>
                ))}
                {canBook && o.isPublished && (
                  <Button variant="contained" size="small" sx={{ mt: 1.5 }}
                    onClick={() => setBooking({ offering: o, packageId: preferredByOffering.get(o.id)?.packageId || '' })}>
                    Book {o.businessName}
                  </Button>
                )}
                <Divider sx={{ my: 1.5 }} />
                <Stack spacing={0.5}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <PlaceIcon fontSize="small" color="action" />
                    <Typography variant="body2">{o.city}{o.serviceArea ? ` · serves ${o.serviceArea}` : ''}</Typography>
                    {distanceLabel(o, near.trim()) && <Chip size="small" variant="outlined" label={distanceLabel(o, near.trim())} />}
                  </Stack>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <PhoneIcon fontSize="small" color="action" />
                    <Typography variant="body2">
                      {o.contactName ? `${o.contactName} · ` : ''}<Link href={`tel:${o.contactPhone}`}>{o.contactPhone}</Link>
                    </Typography>
                  </Stack>
                  {o.contactEmail && (
                    <Stack direction="row" spacing={1} alignItems="center">
                      <EmailIcon fontSize="small" color="action" />
                      <Link variant="body2" href={`mailto:${o.contactEmail}`}>{o.contactEmail}</Link>
                    </Stack>
                  )}
                </Stack>
                {isAdmin && (
                  <Stack direction="row" spacing={1} mt={2} alignItems="center">
                    <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>Vendor account: {o.vendorEmail}</Typography>
                    <Button size="small" onClick={() => setActive({ variables: { id: o.id, isActive: !o.isActive } })}>
                      {o.isActive ? 'Deactivate' : 'Activate'}
                    </Button>
                    <Button size="small" color="error" onClick={() => setConfirmDelete(o)}>Delete</Button>
                  </Stack>
                )}
              </CardContent>
            </Card>
          </Grid>
        ))}</Paged>
      </Grid>

      <BookVendorDialog
        open={Boolean(booking)}
        offering={booking?.offering}
        initialPackageId={booking?.packageId}
        labels={booking ? packageLabelsFor(fieldDefs, booking.offering.category) : null}
        forFamily={bookingFor?.displayName}
        onClose={() => setBooking(null)}
        onBooked={(b, all) => { setBooking(null); setBooked({ ...b, count: all?.length || 1 }); refetchSelections(); }}
      />

      <Dialog open={Boolean(confirmDelete)} onClose={() => setConfirmDelete(null)}>
        <DialogTitle>Delete offering?</DialogTitle>
        <DialogContent>
          <Typography>"{confirmDelete?.title}" by {confirmDelete?.businessName} will be permanently removed.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={() => deleteOffering({ variables: { id: confirmDelete.id } })}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
